import { useEffect, useState, ChangeEvent } from 'react';
import { Stack } from '@mui/material';
import isEmpty from 'lodash/isEmpty';

import { useAgents } from 'src/api/hooks';
import { AgentsTable, AgentsFiltersModal } from 'src/components/Agents';
import { ActionsCard } from 'src/components/TablePage/ActionsCard';
import { Pagination } from 'src/components/Pagination';
import { FilterFormType, AgentFilter } from 'src/types';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { PAGE_SIZE, routes } from 'src/constants';

export const Agents = () => {
    const { fetchAgents, agents, loading, paging, deleteAgent } = useAgents();

    const history = useHistory();
    const MENU_ITEMS = [
        {
            title: 'Add agent',
            onClick: () => history.push(routes.agentsNew)
        },
        {
            title: 'Export CSV'
        }
    ];

    const { totalPages } = paging || {};
    const [pageNumber, setPageNumber] = useState(0);
    const [searchString, setSearchingString] = useState('');

    const { handleSubmit, control, reset } = useForm<FilterFormType>();
    const [filterFormState, setFilterFormState] = useState<FilterFormType>();
    const [filter, setFilter] = useState<string>('');

    const [modalVisible, setModalVisible] = useState(false);
    const hideModal = () => setModalVisible(false);
    const openModal = () => setModalVisible(true);

    const getAgents = (pageNumber: number, innerSearchString?: string) => {
        fetchAgents({
            searchString: innerSearchString ?? searchString,
            pageNumber: `${pageNumber}`,
            pageSize: PAGE_SIZE,
            filter
        });
    };

    useEffect(() => {
        getAgents(pageNumber);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (e: ChangeEvent<unknown>, page: number) => {
        setPageNumber(page - 1);
        getAgents(page - 1);
    };

    const handleSearchChange = (searchString?: string) => {
        setSearchingString(searchString || '');
        getAgents(0, searchString);

        if (pageNumber) {
            setPageNumber(0);
        }
    };

    const handleRefresh = () => {
        setFilterFormState(undefined);
        setFilter('');
        reset({});
        fetchAgents({
            searchString: searchString,
            pageNumber: `${pageNumber}`,
            pageSize: PAGE_SIZE
        });
    };

    const handleDeleteAgent = (id: string) => {
        deleteAgent(id, {
            searchString,
            pageNumber: '0',
            pageSize: PAGE_SIZE
        });
        setPageNumber(0);
    };

    const handleOpenFilterModal = () => {
        openModal();
    };

    const applyFilter = (filter: AgentFilter) => {
        setPageNumber(0);
        setFilter(filter);
        return fetchAgents({
            searchString,
            pageNumber: '0',
            pageSize: PAGE_SIZE,
            filter
        });
    };

    const handleCancelFilter = () => {
        reset(filterFormState);
        hideModal();
    };

    return (
        <Stack spacing={2}>
            <ActionsCard
                onSearchChange={handleSearchChange}
                onRefresh={handleRefresh}
                openFilter={handleOpenFilterModal}
                actionsMenuItems={MENU_ITEMS}
            />

            <AgentsTable
                data={Object.values(agents)}
                loading={loading}
                deleteAgent={handleDeleteAgent}
            />

            {!isEmpty(agents) && (
                <Pagination
                    page={pageNumber + 1}
                    onChange={handlePageChange}
                    count={totalPages}
                />
            )}
            <AgentsFiltersModal
                onClose={hideModal}
                open={modalVisible}
                applyFilter={applyFilter}
                onCancel={handleCancelFilter}
                handleSubmit={handleSubmit}
                setFilterFormState={setFilterFormState}
                control={control}
            />
        </Stack>
    );
};
